import * as motion from "motion/react-client";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";

/**
 * What a card is doing right now.
 *
 * Not a visual variant — each of these is a different fact about the game, and
 * the styling follows from the fact rather than the other way round.
 */
export type CardState =
  /** On a timeline. The year is public and is the whole point of it being there. */
  | "placed"
  /** The round in flight, before `Reveal`. Nobody knows the year, including us. */
  | "unknown"
  /** Somebody has contested this placement. */
  | "challenged"
  /** Just revealed — the moment the year lands. */
  | "revealed";

/**
 * One track on the board.
 *
 * A DOM element rather than canvas, deliberately (see `.claude/rules/frontend.md`):
 * a card is typography, and text layout, focus and accessibility are exactly
 * what canvas is worst at.
 *
 * # The year is optional, and that is the security boundary
 *
 * `year` is `null` before `Reveal` because **the server does not send it**, not
 * because this component hides it. A card asked to render a year it was not
 * given shows `?`; there is nothing here to reach for. Keep it that way — the
 * moment this component takes a wider object than it renders, Critical Rule 7
 * depends on a component remembering something.
 *
 * # No state is signalled by colour alone
 *
 * Every state that matters carries a word as well as a hue, because this is
 * played in a dim room by people who are not looking closely, and about one man
 * in twelve cannot tell the challenge outline from the ordinary one.
 *
 * # It lands rather than appears
 *
 * A card sliding into a timeline is how somebody watching from the sofa sees
 * *where* it went — a card that simply exists in a new position makes the whole
 * row re-read. One spring, on mount, and nothing on idle: this runs on a
 * television browser slower than every phone in the room.
 *
 * `prefers-reduced-motion` is honoured globally in `styles/index.css`, and
 * Motion respects it too. Somebody who asked for less motion is exactly the
 * person a card flying across a large screen was worst for.
 */
export function Card({
  artist,
  title,
  year = null,
  state = "placed",
  compact = false,
  className,
}: {
  /** Who it is by. */
  artist: string;
  /** What it is called. */
  title: string;
  /** When it came out — `null` until the server says. */
  year?: number | null;
  /** What this card is doing. */
  state?: CardState;
  /** Timeline-sized rather than hero-sized. */
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const known = year !== null && year !== undefined;

  return (
    <motion.article
      initial={{ opacity: 0, y: -12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 420, damping: 32 }}
      data-testid="card"
      data-state={state}
      className={cn(
        // **Centred, and nothing is cut off** — 20d's third pass. The text was
        // `truncate`d to hold a fixed square, so *"Bohemian Rhapsody"* read as
        // *"Bohemian …"* and two different songs by one artist could look like
        // the same card. A title you cannot finish reading is not a card you
        // can place with confidence.
        "flex shrink-0 flex-col items-center justify-center gap-1 rounded-xl border bg-stage-900 text-center shadow-lg",
        compact ? "p-2" : "p-3",
        // Finding 3.4: *"way smaller, like half the size they are now."* A
        // timeline card carries a year, a title and an artist, and only the
        // year has to be read across a table — the rest is confirmation for the
        // person holding the phone. 112px → 80px is roughly half the area.
        //
        // ⚠️ **`min-h`, not `h`** — that is what lets the text wrap instead of
        // being cut. 80px is the floor, so a row of short titles still reads as
        // a row of squares; a long one grows and takes its neighbours with it,
        // because a grid row is as tall as its tallest cell.
        //
        // ⚠️ The claimed-slot placeholders in `Timeline.tsx` are sized from this
        // number, not from the gap (phase 18, finding #16). They move with it.
        compact ? "min-h-20 w-20" : "min-h-44 w-40",
        state === "challenged" && "border-wrong-500 ring-2 ring-wrong-500/40",
        state === "revealed" && "border-flash-500 ring-2 ring-flash-500/40",
        state !== "challenged" && state !== "revealed" && "border-white/10",
        className,
      )}
    >
      <p
        className={cn(
          "font-semibold tabular-nums",
          compact ? "text-lg" : "text-4xl",
          known ? "text-flash-400" : "text-white/35",
        )}
      >
        {known ? year : "?"}
        {!known && <span className="sr-only">{t("card.yearHidden")}</span>}
      </p>

      {/* `break-words` rather than a width: a single long word — and band names
          are full of them — has nowhere to wrap otherwise and would push out of
          the card it is meant to fit in. */}
      <div className="w-full min-w-0">
        <p
          className={cn(
            "break-words font-medium",
            compact ? "text-[10px] leading-tight" : "text-sm",
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            "break-words text-white/60",
            compact ? "text-[9px] leading-tight" : "text-xs",
          )}
        >
          {artist}
        </p>
      </div>

      {/* A word, not just a colour. */}
      {state === "challenged" && (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-wrong-500">
          {t("card.challenged")}
        </p>
      )}
    </motion.article>
  );
}
